import { authConfig } from "./auth_config";
import type { AuthProviderName } from "../auth/types";

export interface ApiConfig {
  publicApiUrl: string;
  privateApiUrl: string;
  authApiUrl: string;
}

export interface Auth0Config {
  domain: string;
  clientId: string;
  audience?: string;
}

export interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
}

export function getAuthProviderName(): AuthProviderName {
  // Trim + lowercase so trailing whitespace or case in .env doesn't silently
  // route to the wrong provider (e.g. "keycloak ").
  const raw = (authConfig.provider ?? "").trim().toLowerCase();
  return raw === "keycloak" ? "keycloak" : "auth0";
}

export function getAuth0Config(): Auth0Config {
  // Configure the audience here. By default, it will take whatever is in the config
  // (specified by the `audience` key) unless it's the default value of "YOUR_API_IDENTIFIER" (which
  // is what you get sometimes by using the Auth0 sample download tool from the quickstart page, if you
  // don't have an API).
  // If this resolves to `null`, the API page changes to show some helpful info about what to do
  // with the audience.
  const audience =
    authConfig.audience && authConfig.audience !== "YOUR_API_IDENTIFIER"
      ? authConfig.audience
      : undefined;

  return {
    domain: authConfig.domain ?? "",
    clientId: authConfig.clientId ?? "",
    ...(audience ? { audience } : null),
  };
}

export function getKeycloakConfig(): KeycloakConfig {
  return {
    url: authConfig.keycloak.url ?? "",
    realm: authConfig.keycloak.realm ?? "",
    clientId: authConfig.keycloak.clientId ?? "",
  };
}

// Backwards-compatible alias — older call sites assumed Auth0 only.
export const getConfig = getAuth0Config;

export function getApiConfig(): ApiConfig {
  // For backward compatibility, if only REACT_APP_API is set, use it for both
  const defaultApiUrl = process.env.REACT_APP_API || '';

  return {
    publicApiUrl: process.env.REACT_APP_PUBLIC_API || defaultApiUrl,
    privateApiUrl: process.env.REACT_APP_PRIVATE_API || defaultApiUrl,
    authApiUrl: process.env.REACT_APP_AUTH_API || defaultApiUrl,
  };
}
